import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { Skill } from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { TagType } from '@freelancer/ui/tag';

@Component({
  standalone: false,
  selector: 'fl-skills-selected',
  template: `
    <h3 class="text-body-16 SelectedSkillsHeading">
      @if (maxSkillsLimit) {
        @if (selectedSkills?.length > 0) {
          <ng-container i18n="Number of selected skills heading">
            {{ selectedSkills?.length }} out of {{ maxSkillsLimit }} skills
            selected
          </ng-container>
        } @else {
          <ng-container i18n="No selected skills heading">
            0 skills selected
          </ng-container>
        }
      }
    </h3>

    @if (selectedSkills?.length > 0) {
      <fl-scrollable-content class="SelectedSkillsContent">
        @if (matchingSkillCount) {
          <p
            class="mb-small"
            i18n="Number of jobs matching the skills"
          >
            {{ matchingSkillCount }} jobs matching your skills
          </p>
        }
        @for (skill of selectedSkills; track trackBySkillId($index, skill)) {
          <fl-tag
            class="mb-xxsmall mr-xxsmall"
            [type]="TagType.DISMISSABLE"
            (dismiss)="handleDeselectedSkill(skill)"
          >
            {{ skill.name }}
          </fl-tag>
        }
      </fl-scrollable-content>
    } @else {
      @if (showErrorMessage) {
        <fl-banner-alert
          class="Alert"
          bannerTitle="Please select at least one skill."
          i18n-bannerTitle="No selected skills error message"
          [marginBottom]="'none'"
          [type]="BannerAlertType.ERROR"
          [closeable]="false"
        />
      } @else {
        <p
          class="SelectedSkillsContent"
          i18n="No selected skills message"
        >
          Select at least one skill to help us recommend customized jobs for
          you.
        </p>
      }
    }
  `,
  styleUrls: ['./skills-selected.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsSelectedComponent {
  BannerAlertType = BannerAlertType;
  TagType = TagType;

  @Input() showErrorMessage = false;
  @Input() matchingSkillCount?: number;
  @Input() maxSkillsLimit?: number;
  @Input() selectedSkills?: readonly Skill[] = [];
  @Output() deselectSkill = new EventEmitter<Skill>();

  handleDeselectedSkill(skill: Skill): void {
    this.deselectSkill.emit(skill);
  }

  trackBySkillId(index: number, skill: Skill): number {
    return skill.id;
  }
}
